/**
 * RPDM W1-D · Hybrid Project Side Navigation (structural shell only).
 *
 * SCOPE: Renders the four workspace surfaces (Summary · Delivery ·
 * Team · Settings). Each link is disabled unless its own feature flag
 * is enabled — the master flag has already gated the parent shell so
 * these are sub-surface toggles.
 *
 * `settings` link is additionally gated on the super_admin role
 * (checkpoint §12.4 requirement).
 */
import { NavLink, useParams } from "react-router-dom";
import { House, ListChecks, UsersThree, Gear } from "@phosphor-icons/react";
import { useAuth } from "../../../contexts/AuthContext";
import { useRpdmWorkspaceGate } from "../../../hooks/useRpdmWorkspaceGate";

const ITEMS = [
    {
        key: "summary",
        label: "Summary",
        icon: House,
        path: "",
        gateKey: "summaryLanding",
        testId: "rpdm-sidenav-summary",
    },
    {
        key: "delivery",
        label: "Delivery",
        icon: ListChecks,
        path: "delivery",
        gateKey: "delivery",
        testId: "rpdm-sidenav-delivery",
    },
    {
        key: "team",
        label: "Team",
        icon: UsersThree,
        path: "team",
        gateKey: "team",
        testId: "rpdm-sidenav-team",
    },
    {
        key: "settings",
        label: "Settings",
        icon: Gear,
        path: "settings",
        gateKey: "settings",
        testId: "rpdm-sidenav-settings",
        superAdminOnly: true,
    },
];

export default function ProjectSideNav() {
    const { id } = useParams();
    const { user } = useAuth();
    const gates = useRpdmWorkspaceGate();
    const isSuperAdmin = user?.role === "super_admin";

    return (
        <nav
            data-testid="rpdm-workspace-sidenav"
            className="w-56 shrink-0 border-r border-slate-200 bg-slate-50/60"
            aria-label="Project workspace navigation"
        >
            <ul className="flex flex-col gap-1 p-3 text-sm">
                {ITEMS.map((item) => {
                    if (item.superAdminOnly && !isSuperAdmin) return null;
                    const enabled = gates[item.gateKey] === true;
                    const to = item.path
                        ? `/projects/${id}/${item.path}`
                        : `/projects/${id}`;
                    const Icon = item.icon;

                    if (!enabled) {
                        return (
                            <li key={item.key}>
                                <span
                                    data-testid={`${item.testId}-disabled`}
                                    className="flex cursor-not-allowed items-center gap-2 rounded-md px-3 py-2 text-slate-400"
                                    title={`${item.label} — feature not enabled`}
                                >
                                    <Icon size={16} weight="regular" />
                                    <span>{item.label}</span>
                                </span>
                            </li>
                        );
                    }

                    return (
                        <li key={item.key}>
                            <NavLink
                                data-testid={item.testId}
                                to={to}
                                end={!item.path}
                                className={({ isActive }) =>
                                    `flex items-center gap-2 rounded-md px-3 py-2 transition-colors ${
                                        isActive
                                            ? "bg-slate-900 text-white"
                                            : "text-slate-700 hover:bg-slate-100"
                                    }`
                                }
                            >
                                <Icon size={16} weight="regular" />
                                <span>{item.label}</span>
                            </NavLink>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}
